// v2.0.0 jQueryLite-style utility that dynamically handles single or multiple DOM elements

(function (global) {

  function normalizeSelector(sel){
    return sel
      .replace(/select\s*:selected\b/g,'select option:checked')
      .replace(/option\s*:selected\b/g,'option:checked');
  }

  function querySelector(selector, context = document) {
    const origSelector = selector;
    const elements = typeof selector === 'string'
      ? Array.from(context.querySelectorAll(normalizeSelector(selector)))
      : selector instanceof Node
        ? [selector]
        : selector instanceof NodeList || Array.isArray(selector)
          ? Array.from(selector)
          : [];

    function api(elList) {
      return {
        elements: elList,
        length: elList.length,

        // .ready(): Run callback when the DOM is ready
        // Usage: $q(document).ready(() => { /* DOM ready */ });
        ready(callback) {
          if (typeof callback !== 'function') return this;

          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
          } else {
            callback();
          }

          return this;
        },

        // Core Loop: Executes a callback for each element
        // Usage: $q('.item').forEach((el, i) => console.log(el, i));
        forEach(callback) {
          elList.forEach((el, i) => callback(el, i));
          return this;
        },

        // .each(): Alias for forEach, loops through all elements
        // Usage: $q('.item').each((i, el) => console.log(i, el));
        each(callback) {
          elList.forEach((el, i) => callback.call(el, i, el));
          return this;
        },

        // .val(): Get or set input value
        // Usage:
        // const v = $q('input').val();         // getter
        // $q('input').val('new value');        // setter
        // $q('input').val('');                 // setter empty string
        val(value) {
          if (value === undefined) {
            // Getter: return value of first element
            return elList[0]?.value ?? '';
          } else {
            // Setter: assign value to all elements
            elList.forEach(el => {
              el.value = value;
            });
            return this;
          }
        },

        // .text(): Get or set textContent
        // Usage:
        // const txt = $q('p').text();                  // getter
        // $q('p').text('Hello World');                 // setter
        // const txtOrFallback = $q('p').text('empty'); // getter with fallback
        text(value) {
          if (value === undefined) {
            // Getter: return concatenated text or fallback
            const t = elList.map(el => el.textContent).join(' ');
            return t || '';
          } else {
            // Setter: update textContent for each element
            elList.forEach(el => {
              el.textContent = value;
            });
            return this;
          }
        },

        // .html(): Get concatenated innerHTML
        // Usage: const h = $q('.box').html();
        html(fallback = '') {
          const h = elList.map(el => el.innerHTML).join('');
          return h || fallback;
        },

        // Attributes & Data: Manipulate attributes and data-* properties
        // .attr(): Get attribute or fallback
        // Usage: const href = $q('a').attr('href', '#');
        attr(name, fallback = null) {
          return elList[0]?.getAttribute(name) ?? fallback;
        },

        // .removeAttr(): Remove attribute from all elements
        // Usage: $q('img').removeAttr('alt');
        removeAttr(name) {
          elList.forEach(el => el.removeAttribute(name));
          return this;
        },

        // .setAttribute(): Set attribute on all elements
        // Usage: $q('img').setAttribute('alt', 'Image');
        setAttribute(name, value) {
          elList.forEach(el => el.setAttribute(name, value));
          return this;
        },

        // .hasAttribute(): Check if any element has the attribute
        // Usage: const has = $q('input').hasAttribute('required');
        hasAttribute(name) {
          return elList.some(el => el.hasAttribute(name));
        },

        // .data(): Get or set data-* attributes
        // Usage: $q('.item').data('id'); or $q('.item').data('id', '123');
        /* data(name, value) {
          if (value === undefined) return elList[0]?.dataset[name];
          elList.forEach(el => { el.dataset[name] = value; });
          return this;
        }, */
        // .data(): Get or set data-* attributes
        // Usage:
        // $q('.item').data('id');
        // $q('.item').data('id', '123');
        // $q('.item').data('cd-option-price', '9.99');
        data(name, value) {
          const key = String(name).replace(/^data-/, '').replace(/-([a-z])/g, function(_, c) {
            return c.toUpperCase();
          });

          if (value === undefined) {
            return elList[0]?.dataset[key];
          }

          elList.forEach(el => {
            el.dataset[key] = value;
          });

          return this;
        },

        // .prop(): Get or set DOM properties
        // Usage: $q('input').prop('checked'); or $q('input').prop('checked', true);
        prop(name, value) {
          if (value === undefined) return elList[0]?.[name];
          elList.forEach(el => { el[name] = value; });
          return this;
        },

        // .css(): Get or set CSS styles
        // Usage: $q('.box').css('color'); // get
        //        $q('.box').css('color', 'red'); // set single
        //        $q('.box').css({ color: 'blue', fontSize: '18px' }); // set multiple
        css(prop, value) {
          if (typeof prop === 'string') {
            if (value === undefined) {
              // Get computed style
              const el = elList[0];
              return el ? getComputedStyle(el)[prop] : undefined;
            } else {
              // Set single style
              elList.forEach(el => { el.style[prop] = value; });
            }
          } else if (typeof prop === 'object') {
            // Set multiple styles
            for (const key in prop) {
              elList.forEach(el => { el.style[key] = prop[key]; });
            }
          }
          return this;
        },

        // .offset(): Get the current coordinates of the first element relative to the document
        // Usage:
        // const top = $q('.box').offset().top;
        // const left = $q('#target').offset().left;
        offset() {
          const el = elList[0];
          if (!el || !el.getBoundingClientRect) return { top: 0, left: 0, bottom: 0, right: 0 };

          const rect = el.getBoundingClientRect();
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          return {
            top: rect.top + scrollTop,
            left: rect.left + scrollLeft,
            bottom: rect.bottom + scrollTop,
            right: rect.right + scrollLeft
          };
        },

        // Manipulation: Clone, wrap, unwrap, replace, remove elements

        // .clone(): Deep copy elements
        // Usage: const clones = $q('.card').clone();
        clone() {
          const clones = elList.map(el => el.cloneNode(true));
          return $q(clones);
        },

        // .wrap(): Wrap each element with provided HTML
        // Usage: $q('.item').wrap('<div class="wrapper"></div>');
        wrap(wrapperHTML) {
          elList.forEach(el => {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = wrapperHTML.trim();
            const w = wrapper.firstChild;
            el.parentNode.insertBefore(w, el);
            w.appendChild(el);
          });
          return this;
        },

        // .unwrap(): Remove parent element and preserve children
        // Usage: $q('.wrapper').unwrap();
        unwrap() {
          elList.forEach(el => {
            const parent = el.parentNode;
            if (!parent || parent === document.body) return;
            while (el.firstChild) parent.insertBefore(el.firstChild, el);
            parent.removeChild(el);
          });
          return this;
        },

        // .replaceWith(): Replace element with HTML
        // Usage: $q('.old').replaceWith('<div class="new">New</div>');
        replaceWith(html) {
          elList.forEach(el => {
            el.insertAdjacentHTML('beforebegin', html);
            el.remove();
          });
          return this;
        },

        // .remove(): Remove elements from DOM
        // Usage: $q('.temp').remove();
        remove() {
          elList.forEach(el => el.remove());
          return this;
        },

        // Show / Hide / Toggle: Control visibility
        // .hide(): Hide elements
        // Usage: $q('.modal').hide();
        hide() {
          elList.forEach(el => el.style.display = 'none');
          return this;
        },

        // .show(): Show elements with optional display type
        // Usage: $q('.modal').show('flex');
        show(display = 'block') {
          elList.forEach(el => el.style.display = display);
          return this;
        },

        // .toggle(): Toggle visibility, optional force boolean
        // Usage: $q('.menu').toggle(); or $q('.menu').toggle(true);
        toggle(force) {
          elList.forEach(el => {
            if (force !== undefined) el.style.display = force ? '' : 'none';
            else el.style.display = (getComputedStyle(el).display === 'none') ? '' : 'none';
          });
          return this;
        },

        // .toggleClass(): Toggle a class name, optionally using a boolean state
        // @param {string} className - The class to toggle
        // @param {boolean} [state] - Optional boolean to explicitly add (true) or remove (false)
        // @returns {object} this - for chaining
        // Usage:
        // $q('.box').toggleClass('active');          // toggle normally
        // $q('.box').toggleClass('active', true);    // force add
        // $q('.box').toggleClass('active', false);   // force remove
        toggleClass(className, state) {
            elList.forEach(el => {
                if (typeof state === 'boolean') {
                    el.classList.toggle(className, state);
                } else {
                    el.classList.toggle(className);
                }
            });
            return this;
        },

        // Classes: Add, remove, toggle, check class names
        // .addClass(): Add one or more classes, safely ignore empty/invalid entries
        // Usage: $q('.btn').addClass('active', 'highlight');
        addClass(...classes) {
          const tokens = [];
          const collect = (v) => {
            if (Array.isArray(v)) { v.forEach(collect); return; }
            if (typeof v === 'string') {
              const t = v.trim();
              if (t) tokens.push(...t.split(/\s+/)); // support space-separated classes
            }
          };
          classes.forEach(collect);
          if (tokens.length === 0) return this;

          elList.forEach(el => {
            tokens.forEach(cls => el.classList.add(cls));
          });
          return this;
        },

        // .removeClass(): Remove one or more classes, safely ignore empty/invalid entries
        // Usage: $q('.btn').removeClass('active');
        removeClass(...classes) {
          const tokens = [];
          const collect = (v) => {
            if (Array.isArray(v)) { v.forEach(collect); return; }
            if (typeof v === 'string') {
              const t = v.trim();
              if (t) tokens.push(...t.split(/\s+/)); // support space-separated classes
            }
          };
          classes.forEach(collect);
          if (tokens.length === 0) return this;

          elList.forEach(el => {
            tokens.forEach(cls => el.classList.remove(cls));
          });
          return this;
        },
        
        // .toggleClass(): Toggle class with optional forced state and timeout
        // Usage: $q('.btn').toggleClass('open');
        toggleClass(cls, force, timeout) {
          elList.forEach(el => {
            el.classList.toggle(cls, force);
            if (typeof timeout === 'number') setTimeout(() => el.classList.toggle(cls, !force), timeout);
          });
          return this;
        },

        // .hasClass(): Check if any element has the given class
        // Usage: const has = $q('.item').hasClass('active');
        hasClass(cls) {
          return elList.some(el => el.classList.contains(cls));
        },

        // .on(): Add event handler(s) (supports space-separated events, direct or delegated)
        // Usage: $q('.btn').on('click focus', handler);
        //        $q('ul').on('click focus', 'li', handler);
        // .on(): direct or delegated (space-separated events supported)
        on(events, selectorOrHandler, maybeOpts, options) {
          const eventList = events.split(/\s+/);
          const isDelegated = typeof selectorOrHandler === 'string';
          const sel = isDelegated ? selectorOrHandler : null;
          const handler = isDelegated ? maybeOpts : selectorOrHandler;
          const opts = (isDelegated ? options : maybeOpts) || {};

          // default to capture=true for delegated so stopPropagation in bubble doesn't break us
          const delegatedOpts = Object.assign({ capture: true }, opts);

          const getPath = (e) => e.composedPath ? e.composedPath() : (function(){
            const p = []; let n = e.target;
            while (n) { p.push(n); n = n.parentNode || n.host; } // walk up, include shadow host
            return p;
          })();

          eventList.forEach(event => {
            elList.forEach(parent => {
              if (isDelegated) {
                parent.addEventListener(event, function(e){
                  // Guard: ignore non-Elements safely
                  const path = getPath(e);
                  for (const node of path) {
                    if (!node || node === parent) break;
                    if (node.nodeType === 1 && node.matches && node.matches(sel)) {
                      handler.call(node, e);
                      return;
                    }
                  }
                }, delegatedOpts);
              } else {
                parent.addEventListener(event, handler, opts);
              }
            });
          });

          return this;
        },
        /* on(events, selectorOrHandler, maybeOpts, options) {
          const eventList = events.split(/\s+/); // split by whitespace

          if (typeof selectorOrHandler === 'string') {
            const sel = selectorOrHandler;
            const handler = maybeOpts;

            eventList.forEach(event => {
              elList.forEach(parent => {
                parent.addEventListener(event, function (e) {
                  const t = e.target.closest(sel);
                  if (t && parent.contains(t)) handler.call(t, e);
                }, options);
              });
            });

          } else {
            const handler = selectorOrHandler;

            eventList.forEach(event => {
              elList.forEach(el => {
                el.addEventListener(event, handler, maybeOpts);
              });
            });
          }

          return this;
        }, */

        // .off(): Remove event handler(s)
        // Supports single or multiple events (space-separated)
        // Usage:
        // $q('.btn').off('click', handler);
        // $q('.input').off('focus blur', handler);
        off(events, handler, options) {
          const eventList = events.split(/\s+/);
          elList.forEach(el => {
            eventList.forEach(evt => {
              el.removeEventListener(evt, handler, options);
            });
          });
          return this;
        },

        // .trigger(): Trigger a native event on all matched elements
        // Usage: $q('.btn').trigger('click');
        //        $q('.input').trigger(new Event('input'));
        /* trigger(event) {
          elList.forEach(el => {
            const ev = typeof event === 'string' ? new Event(event, { bubbles: true, cancelable: true }) : event;
            el.dispatchEvent(ev);
          });
          return this;
        }, */
        // .trigger(): Robust event trigger using proper constructors (with click fallback)
        // Usage:
        // $q('.btn').trigger('click');
        // $q('input').trigger('input', { data:'x' });
        // $q('.box').trigger(new Event('custom'));
        // $q('.item').trigger('keydown', { key: 'Enter' });
        trigger(typeOrEvent, init = {}) {
          const map = {
            click: 'MouseEvent', dblclick: 'MouseEvent', contextmenu: 'MouseEvent',
            mousedown: 'MouseEvent', mouseup: 'MouseEvent', mousemove: 'MouseEvent',
            wheel: 'WheelEvent',
            pointerdown: 'PointerEvent', pointerup: 'PointerEvent', pointermove: 'PointerEvent',
            pointerenter: 'PointerEvent', pointerleave: 'PointerEvent',
            keydown: 'KeyboardEvent', keyup: 'KeyboardEvent', keypress: 'KeyboardEvent',
            input: 'InputEvent', change: 'Event', submit: 'Event',
            focus: 'FocusEvent', blur: 'FocusEvent'
          };
          const common = { bubbles: true, cancelable: true, composed: true, ...init };

          this.elements.forEach(el => {
            if (typeOrEvent instanceof Event) { el.dispatchEvent(typeOrEvent); return; }

            const type = String(typeOrEvent);
            // Native shortcut triggers when possible
            if ((type === 'click' || type === 'submit') && typeof el[type] === 'function' && Object.keys(init).length === 0) {
              el[type](); return;
            }

            let evt, Ctor = map[type] || 'CustomEvent';
            try {
              if (Ctor === 'CustomEvent') evt = new CustomEvent(type, { detail: init.detail, ...common });
              else if (Ctor === 'MouseEvent') evt = new MouseEvent(type, common);
              else if (Ctor === 'KeyboardEvent') evt = new KeyboardEvent(type, common);
              else if (Ctor === 'FocusEvent') evt = new FocusEvent(type, common);
              else if (Ctor === 'InputEvent') evt = new InputEvent(type, common);
              else if (Ctor === 'PointerEvent') evt = new PointerEvent(type, common);
              else if (Ctor === 'WheelEvent') evt = new WheelEvent(type, common);
              else evt = new Event(type, common);
            } catch(_) {
              evt = new Event(type, common);
            }
            el.dispatchEvent(evt);
          });
          return this;
        },

        // .click(): Shorthand for triggering a click event (or native .click())
        click() {
          this.elements.forEach(el => {
            if (typeof el.click === 'function') {
              el.click(); // native behavior (works for <button>, <input>, etc.)
            } else {
              el.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
            }
          });
          return this;
        },

        // .change(): Shorthand for triggering a change event
        change() {
          this.elements.forEach(el => {
            el.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
          });
          return this;
        },

        // Animation & Scrolling: Web Animations API & smooth scroll

        // .animate(): Animate using keyframes and options, optional callback
        // Usage: $q('.fade').animate([{opacity:0},{opacity:1}], {duration:500}, () => {});
        animate(keyframes, opts, callback) {
          elList.forEach((el, i) => {
            const anim = el.animate(keyframes, opts);
            if (i === elList.length - 1 && typeof callback === 'function') anim.finished.then(callback);
          });
          return this;
        },

        // Smooth Scroll (old way)
        // .scrollTo(): Scroll first element into view, optional callback
        // (final, duration, cb)
        // $q(window).scrollTo(scrollOffsetTop, 500);
        scrollUp(final, duration, cb) {

          var start = window.scrollY || document.documentElement.scrollTop;
          var currentTime = null;              
          var animateScroll = function(timestamp){
            if (!currentTime) { currentTime = timestamp;}
            var progress = timestamp - currentTime;
            if(progress > duration) {progress = duration;}
            var val = Math.easeInOutQuad(progress, start, final-start, duration);
            window.scrollTo(0, val);
            if(progress < duration) {
                window.requestAnimationFrame(animateScroll);
            } else {
              cb && cb();
            }
          };
          window.requestAnimationFrame(animateScroll);
        },

        // .scrollTop(): Get or set the vertical scroll position of the first element or window
        // Usage: 
        // const y = $q(window).scrollTop();      // Get scroll position of window
        // const y = $q('.box').scrollTop();      // Get scroll position of an element
        // $q(window).scrollTop(200);             // Scroll window to 200px
        // $q('.box').scrollTop(100);             // Scroll .box element to 100px
        scrollTop(value) {
          const el = this.elements[0];
          if (!el) return 0;

          if (value === undefined) {
            // Getter
            if (el === window || el === document) {
              return window.pageYOffset || document.documentElement.scrollTop || 0;
            } else {
              return el.scrollTop;
            }
          } else {
            // Setter
            this.elements.forEach(el => {
              if (el === window || el === document) {
                window.scrollTo({ top: value });
              } else {
                el.scrollTop = value;
              }
            });
            return this;
          }
        },

        // .fadeIn(): Fade in elements
        // Usage: $q('.box').fadeIn(300);
        fadeIn(duration = 400, display = 'block') {
          elList.forEach(el => {
            el.style.opacity = 0;
            el.style.display = display;
            el.animate([{ opacity: 0 }, { opacity: 1 }], { duration });
            setTimeout(() => (el.style.opacity = 1), duration);
          });
          return this;
        },

        // .fadeOut(): Fade out elements
        // Usage: $q('.box').fadeOut(300);
        fadeOut(duration = 400) {
          elList.forEach(el => {
            el.animate([{ opacity: 1 }, { opacity: 0 }], { duration });
            setTimeout(() => { el.style.opacity = 0; el.style.display = 'none'; }, duration);
          });
          return this;
        },

        // .slideUp(): Slide up hide
        // Usage: $q('.panel').slideUp(300);
        slideUp(duration = 400) {
          elList.forEach(el => {
            el.style.overflow = 'hidden';
            const h = el.offsetHeight + 'px';
            el.style.height = h; el.offsetHeight;
            el.style.transition = `height ${duration}ms`;
            el.style.height = '0';
            setTimeout(() => { el.style.display = 'none'; el.style.height = ''; el.style.transition = ''; el.style.overflow = ''; }, duration);
          });
          return this;
        },

        // .slideDown(): Slide down show
        // Usage: $q('.panel').slideDown(300);
        slideDown(duration = 400, display = 'block') {
          elList.forEach(el => {
            el.style.display = display;
            const h = el.scrollHeight + 'px';
            el.style.overflow = 'hidden'; el.style.height = '0'; el.offsetHeight;
            el.style.transition = `height ${duration}ms`;
            el.style.height = h;
            setTimeout(() => { el.style.height = ''; el.style.transition = ''; el.style.overflow = ''; }, duration);
          });
          return this;
        },

        // .slideToggle(): Toggle between slideUp and slideDown
        // Usage: $q('.panel').slideToggle(300);
        slideToggle(duration = 400, display = 'block') {
          elList.forEach(el => { const hidden = getComputedStyle(el).display === 'none'; hidden ? this.slideDown(duration, display) : this.slideUp(duration); });
          return this;
        },

        // .wait(): Wait for element(s) to appear in DOM, then run a callback (no .then() needed)
        // Usage:
        // $q('.selector').wait(true, (elm) => { /* do stuff */ });
        // $q('.selector').wait(false, function(elm){ /* do stuff */ });
        wait(disconnect = false, callback) {
          const targetSelector = (typeof origSelector === 'string') ? origSelector : null;
          if (!targetSelector) return this;

          const run = (el) => { if (typeof callback === 'function') callback.call(el, el); };

          const existing = document.querySelector(targetSelector);
          if (existing) { run(existing); return this; }

          const observer = new MutationObserver(() => {
            const el = document.querySelector(targetSelector);
            if (el) {
              run(el);
              if (disconnect === true) observer.disconnect();
            }
          });

          observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter:["class","checked","id","disabled"] });
          return this;
        },
        //wait(s,d=!0){return new Promise(e=>{let t=document.querySelector(s);if(t)return e(t);const o=new MutationObserver(()=>{let t=document.querySelector(s);t&&(d&&o.disconnect(),e(t))});o.observe(document.documentElement,{childList:!0,subtree:!0,attributes:!0,attributeFilter:["class","checked","id","disabled"]})})},


        // Traversal: Navigate the DOM tree

        // .parent(): Get direct parent elements
        // Usage: $q('.child').parent().addClass('highlight');
        parent() { return $q(elList.map(el => el.parentElement).filter(Boolean)); },

        // .parents(): Get ancestor elements matching selector until optional stopAt
        // Usage: $q('.item').parents('ul');
        parents(selector, stopAt = null) {
          const out = [];
          elList.forEach(el => { let node = el.parentElement; while(node) { if (stopAt && node.matches(stopAt)) break; if (node.matches(selector)) { out.push(node); break; } node=node.parentElement; }});
          return $q(out);
        },

        // .find(): Find descendant elements matching selector
        // Usage: $q('.container').find('.item');
        find(sel) { return $q(elList.flatMap(el => Array.from(el.querySelectorAll(sel)))); },

        // .next(): Get the immediate next sibling elements
        // Usage: $q('.item').next().addClass('highlight');
        next() {
          const siblings = elList.map(el => el.nextElementSibling).filter(el => el);
          return $q(siblings);
        },

        // .prev(): Get the immediate previous sibling elements
        // Usage: $q('.item').prev().addClass('highlight');
        prev() {
          const siblings = elList.map(el => el.previousElementSibling).filter(el => el);
          return $q(siblings);
        },

        // .nextAll(): Get all following sibling elements
        // Usage: $q('.item').nextAll().addClass('follow');
        nextAll() {
          const siblings = [];
          elList.forEach(el => {
            let nxt = el.nextElementSibling;
            while (nxt) {
              siblings.push(nxt);
              nxt = nxt.nextElementSibling;
            }
          });
          return $q(siblings);
        },

        // .prevAll(): Get all preceding sibling elements
        // Usage: $q('.item').prevAll().addClass('before');
        prevAll() {
          const siblings = [];
          elList.forEach(el => {
            let prv = el.previousElementSibling;
            while (prv) {
              siblings.push(prv);
              prv = prv.previousElementSibling;
            }
          });
          return $q(siblings);
        },

        // .nextUntil(): Get all next siblings until a selector is matched
        // Usage: $q('.start').nextUntil('.stop');
        nextUntil(selector) {
          const results = [];
          elList.forEach(el => {
            let sibling = el.nextElementSibling;
            while (sibling && !sibling.matches(selector)) {
              results.push(sibling);
              sibling = sibling.nextElementSibling;
            }
          });
          return $q(results);
        },

        // .prevUntil(): Get all previous siblings until a selector is matched
        // Usage: $q('.end').prevUntil('.start');
        prevUntil(selector) {
          const results = [];
          elList.forEach(el => {
            let sibling = el.previousElementSibling;
            while (sibling && !sibling.matches(selector)) {
              results.push(sibling);
              sibling = sibling.previousElementSibling;
            }
          });
          return $q(results);
        },

        // .contents(): Get all child nodes (including text/comment nodes)
        // Usage: $q('.container').contents();
        contents() {
          const result = [];
          elList.forEach(el => result.push(...el.childNodes));
          return $q(result);
        },

        // .siblings(): Get all siblings of each element (excluding itself)
        // Usage: $q('.item').siblings(); or $q('.item').siblings('.active');
        siblings(selector) {
          const results = [];
          elList.forEach(el => {
            const allSiblings = Array.from(el.parentElement?.children || []);
            allSiblings.forEach(sib => {
              if (sib !== el && (!selector || sib.matches(selector))) {
                results.push(sib);
              }
            });
          });
          return $q(results);
        },


        // .closest(): Get closest ancestor matching selector
        // Usage: $q('.item').closest('.container');
        closest(selector) {
          const results = elList.map(el => el.closest(selector)).filter(Boolean);
          return $q(results);
        },

        // .children(): Get all child elements
        // Usage: $q('.parent').children(); or $q('.parent').children('.highlight');
        children(selector) {
          const result = [];
          elList.forEach(el => {
            const kids = Array.from(el.children);
            result.push(...(selector ? kids.filter(child => child.matches(selector)) : kids));
          });
          return $q(result);
        },

        // Utilities: Helper methods
        // .exists(): Check if any matched elements exist
        // Usage: if ($q('.btn').exists()) { ... }
        exists() { return elList.length > 0; },

        // .eq(): Get element at specific index as a new $q object
        // Usage: $q('.items').eq(2).addClass('third');
        eq(i) { return $q(elList[i]); },

        // .index(): Get index of first element among its siblings
        // Usage: const idx = $q('.current').index();
        index() { const el = elList[0]; return !el||!el.parentElement? -1 : Array.from(el.parentElement.children).indexOf(el); },

        // .first(): Get the first matched element
        // Usage: $q('.items').first().addClass('first');
        first() { return this.eq(0); },

        // .last(): Get the last matched element
        // Usage: $q('.items').last().addClass('last');
        last() { return this.eq(elList.length - 1); },

        // .isVisible(): Check if first element is visible
        // Usage: if ($q('#modal').isVisible()) { ... }
        isVisible() { const el = elList[0]; if(!el) return false; const s = getComputedStyle(el); return s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'; },

        // .isHidden(): Check if first element is hidden
        // Usage: if ($q('#modal').isHidden()) { ... }
        isHidden() { return !this.isVisible(); },

        // Insertion: Insert HTML around elements
        // .append(): Insert HTML at the end of elements
        // Usage: $q('.list').append('<li>New</li>');
        append(html) { elList.forEach(el => el.insertAdjacentHTML('beforeend', html)); return this; },

        // .prepend(): Insert HTML at the start of elements
        // Usage: $q('.list').prepend('<li>First</li>');
        prepend(html) { elList.forEach(el => el.insertAdjacentHTML('afterbegin', html)); return this; },

        // .after(): Insert HTML immediately after each element
        // Usage: $q('.item').after('<div>after</div>');
        after(html) { elList.forEach(el => el.insertAdjacentHTML('afterend', html)); return this; },

        // .before(): Insert HTML immediately before each element
        // Usage: $q('.item').before('<div>before</div>');
        before(html) { elList.forEach(el => el.insertAdjacentHTML('beforebegin', html)); return this},

        // .is(): Check if the first element matches a selector, element, or function
        // Usage:
        // $q('.box').is('.active')        → true/false
        // $q('#item').is(otherEl)         → true/false
        // $q('li').is((i, el) => el.id)   → true/false
        is(selector) {
          const el = elList[0];
          if (!el) return false;

          if (typeof selector === 'string') {

            return el.matches(selector);
          } else if (selector instanceof Element) {
            return el === selector;
          } else if (typeof selector === 'function') {
            return selector.call(el, 0, el);
          } else if (selector instanceof $q) {
            return selector.elements.includes(el);
          }

          return false;
        },

        // .not(): Remove elements that match the selector or function
        // Usage: $q('.item').not('.exclude'); 
        //        $q('.item').not((i, el) => el.classList.contains('skip'));
        not(selectorOrFn) {
          let filtered = [];

          if (typeof selectorOrFn === 'function') {
            filtered = elList.filter((el, i) => !selectorOrFn.call(el, i, el));
          } else if (typeof selectorOrFn === 'string') {
            filtered = elList.filter(el => !el.matches(selectorOrFn));
          } else if (selectorOrFn instanceof Node || Array.isArray(selectorOrFn) || selectorOrFn instanceof NodeList) {
            const exclude = Array.from(selectorOrFn);
            filtered = elList.filter(el => !exclude.includes(el));
          }

          return $q(filtered);
        },

        // .get(): Get the raw DOM element at an index
        // Usage: $q('.item').get(0)
        get(i = 0) {
          return this.elements[i];
        },

        // .filter(): Reduce elements to those that match selector or pass a test
        // Usage:
        // $q('li').filter('.active') → only .active lis
        // $q('div').filter((i, el) => el.textContent.includes('yes'))
        filter(selectorOrFn) {
          let filtered = [];
          if (typeof selectorOrFn === 'string') {
            filtered = elList.filter(el => el.matches(selectorOrFn));
          } else if (typeof selectorOrFn === 'function') {
            filtered = elList.filter((el, i) => selectorOrFn.call(el, i, el));
          }
          return $q(filtered);
        },

        // .has(): Keep elements that contain the given selector
        // Usage:
        // $q('ul').has('li') → only uls that contain <li>
        has(selector) {
          const filtered = elList.filter(el => el.querySelector(selector));
          return $q(filtered);
        },

        // .end(): Return to the previous $q() set (if stored in .prevObject)
        // Usage: $q('ul li').filter('.active').end(); // → returns all 'ul li'
        end() {
          return this.prevObject || $q([]);
        },

        // .map(): Map over elements and return array of results
        // Usage: const texts = $q('li').map(el => el.textContent);
        map(callback) {
          return elList.map((el, i) => callback.call(el, el, i));
        },

        // .toArray(): Return raw array of matched DOM elements
        // Usage: const rawEls = $q('.item').toArray();
        toArray() {
          return elList.slice();
        },

        // .trim(): jQuery-style instance method to trim each element’s textContent
        // Usage: $q('.item').trim();
        trim() {
          const rtrim = /^[\s\uFEFF\xA0]+|([^\s\uFEFF\xA0])[\s\uFEFF\xA0]+$/g;
          elList.forEach(el => {
            if (el.textContent != null) {
              el.textContent = (el.textContent + '').replace(rtrim, '$1');
            }
          });
          return this;
        },

        // .fetch(): Perform an HTTP request using the Fetch API (with support for callbacks)
        // Usage:
        // $q().fetch({
        //   url: '/api/data',
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ key: 'value' }),
        //   success: (data) => console.log('Success:', data),
        //   error: (err) => console.error('Error:', err)
        // });
        fetch({
          url,
          method = 'GET',
          headers = {},
          body = null,
          success = () => {},
          error = () => {},
          responseType = 'json', // 'json', 'text', 'blob', etc.
          credentials = 'same-origin',
          cache = 'no-cache',
          mode = 'cors',
          redirect = 'follow',
          referrerPolicy = 'no-referrer'
        } = {}) {
          const inferredMethod = method || (body ? 'POST' : 'GET');
          fetch(url, {inferredMethod,headers,body,credentials,cache,mode,redirect,referrerPolicy
          })
            .then(async res => {
              const parsed = await res[responseType]();
              if (res.ok) {
                success(parsed, res.status, res);
              } else {
                throw { message: 'Request failed', status: res.status, response: parsed };
              }
            })
            .catch(err => {
              error(err, err.status || 0);
            });

          return this;
        }

      };
    }

    // Replace your final "return api(elements);" with this:
    const inst = api(elements);
    return new Proxy(inst,{
      get(target, prop, receiver) {
        if (prop === Symbol.iterator) return target.elements[Symbol.iterator].bind(target.elements);
        if (typeof prop === "string" && /^\d+$/.test(prop)) return target.elements[Number(prop)];
        return Reflect.get(target, prop, receiver);
      }
    });
    //return api(elements);
  }

  // Live (delegated) event helper
  function onLiveEvent(parentSel, evt, childSel, handler, opts) {
    const parent = document.querySelector(parentSel) || document.body;
    parent.addEventListener(evt, function (e) {
      const t = e.target.closest(childSel);
      if (t && parent.contains(t)) handler.call(t, e);
    }, opts);
  }

  // expose:
  global.querySelector = querySelector; // ← your new “main” name
  global.$q = querySelector; // ← alias
  global.onLiveEvent = onLiveEvent;
})(typeof window !== 'undefined' ? window : this);
